import { createContext, useState, useEffect, useCallback, useRef } from "react";
import i18n from "i18next";
import {
  apiGetRequest,
  apiPostRequest,
  apiPutRequest,
} from "../Helpers/Requests";
import endpoints from "../Helpers/Endpoints";
import { createOnboardingAppUrl } from "../Helpers/Actions";
import emitter from "../Utils/events/eventBus";
import { PAGE_VIEWS } from "../Constants";
import { communityRoutes, supplierRoutes } from "../Routes/routePaths";
// ⚠️ TEMP DEV-PHASE PREVIEW — remove with the KYC preview switcher.
import { readKycPreviewFlag, getPreviewKycStatus } from "../Constants/kycPreview";

const normalizePath = (path) => {
  if (!path) return "/";
  return path.replace(/\/+$/, "") || "/";
};

// Interface language (COMY1-397). The UI switches optimistically and the
// choice is then saved with PUT sidebar/language. While that PUT has not been
// confirmed, the chosen code sits in PENDING_LANGUAGE_KEY — see the startup
// rule in fetchData for how it is used.
const PENDING_LANGUAGE_KEY = "appLanguagePending";

const normalizeLangCode = (code) => (code || "").trim().toLowerCase();

const findLanguageByCode = (langs, code) =>
  langs.find((l) => normalizeLangCode(l.code) === normalizeLangCode(code));

/** Show the app in `code` and remember it (DirectionContext reads it on load). */
const applyLanguage = (code) => {
  i18n.changeLanguage(code);
  localStorage.setItem("appLanguage", code);
  document.documentElement.lang = code;
};

// ⚠️ TEMP DEV-PHASE PREVIEW — the language a preview seed renders in: the
// `?lang=` parameter when given, otherwise the language already shown, and
// Hebrew only as the last resort. Previews are opt-in and never run for a
// normal session; this only stops a remembered preview (kycPreview) from
// flipping the app back to Hebrew on every page load.
const getPreviewLanguage = (params) => {
  const param = params.get("lang");
  if (param === "en" || param === "he") return param;
  return localStorage.getItem("appLanguage") === "en" ? "en" : "he";
};

export const AppContext = createContext({});

export const AppProvider = ({ children }) => {
  const [sidebarData, setSidebarData] = useState({});
  const [languages, setLanguages] = useState([]);
  const [isCommunityManager, setisCommunityManager] = useState(false);
  const [isCommunityManagementRole, setisCommunityManagementRole] =
    useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profileData, setProfileData] = useState({});
  const [getStartedContent, setGetStartedContent] = useState({});
  const [userCommunities, setUserCommunities] = useState([]);
  // Where the supplier stands in Grow's business verification. Everything that
  // reaches a community reads this one value; the backend serves it on the
  // sidebar payload as `kyc_status`.
  const [kycStatus, setKycStatus] = useState(null);

  /**
   * Re-read the supplier's KYC state. Grow's form is filled in inside a window
   * of ours and submitted to them directly, so the only way we learn it moved
   * on is by asking — which is what the details window does when it closes.
   */
  const refreshKycStatus = useCallback(async () => {
    try {
      const response = await apiGetRequest(endpoints.getSidebarData);
      setKycStatus(response?.data?.kyc_status ?? null);
    } catch (error) {
      // Leave the state as it is — a failed refresh must not open anything up.
    }
  }, []);
  const [systemError, setSystemError] = useState(null); // New state for system errors

  const fetchGetStartedContent = useCallback(async () => {
    try {
      const url = `${endpoints.getStartedTasks}`;
      const response = await apiGetRequest(url);
      // ⚠️ TEMP (FE-first "Grow your community") — the backend never sends
      // weeklyTasksPreview, so keep it across refetches (e.g. clicking the
      // sidebar card) or the weekly card flips back to Get Started.
      setGetStartedContent((prev) =>
        prev?.weeklyTasksPreview
          ? { ...response.data, weeklyTasksPreview: true }
          : response.data,
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch get started content:", error);
      return null;
    }
  }, []);

  const fetchUserCommunities = useCallback(async () => {
    try {
      const url = `${endpoints.getUserCommunities}`;
      const response = await apiGetRequest(url);
      const communities = response.data.communities;
      setUserCommunities(communities);
    } catch (error) {
      console.error("Failed to fetch user communities:", error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentRoutePathName = normalizePath(window.location.pathname);
        // ⚠️ TEMP DEV-PHASE PREVIEW — see the KYC block below.
        const isKycPreview = readKycPreviewFlag();

        const isManagersHubRoute = currentRoutePathName === "/managers-hub";

        // TEMP dev-only preview seed for the Manager-of-Managers hub FE-first
        // preview — lets /managers-hub render without auth/backend, in the real
        // CM shell (the sidebar portfolio entries need multiple communities).
        // Opt-in via `?preview=1` only: seeding it for real users replaced their
        // account with a fake CM-only one, so they couldn't get back to their
        // supplier / community views. REMOVE with the feature.
        const hubParams = new URLSearchParams(window.location.search);
        if (isManagersHubRoute && hubParams.get("preview") === "1") {
          const params = hubParams;
          const lang = params.get("lang") === "en" ? "en" : "he";
          if (localStorage.getItem("appLanguage") !== lang) {
            localStorage.setItem("appLanguage", lang);
            i18n.changeLanguage(lang);
            window.location.reload();
            return;
          }
          localStorage.setItem("isCommunityManagementRole", "true");
          setisCommunityManagementRole(true);
          setSidebarData({
            communityName: "מגדלי נווה שאנן",
            communityImage: "/mysupplier.png",
            last_role_view: "COMMUNITY_MANAGER",
            communitiesTosUpdates: [],
            // Mock role flags — a Manager-of-Managers, currently in MoM view.
            // In prod these come from the backend (isMoM is Keilot-granted).
            isMoM: true,
            momView: true,
            isCommunityManager: true,
          });
          setUserCommunities([
            { id: 1, name: "מגדלי נווה שאנן", image_url: "/mysupplier.png" },
            { id: 2, name: "מעונות פארק הירקון", image_url: "/supplier-icon.png" },
            { id: 3, name: "כפר סבא ירוקה", image_url: "/people.png" },
          ]);
          setIsLoading(false);
          return;
        }

        // ⚠️ TEMP dev-only preview for "Grow your community" (FE-first) — lets
        // the page, the Get Started popup and the dashboard card be clicked
        // through without auth/backend. `weeklyTasksPreview` is never sent by the backend, so real
        // users keep today's Get Started. Opt-in via `?preview=1` only: seeding
        // it for real users swapped their community for the fake one on every
        // full load (e.g. the week switcher). Remove at graduation.
        const isGrowCommunityRoute = currentRoutePathName === "/grow-community";
        const growParams = new URLSearchParams(window.location.search);
        if (isGrowCommunityRoute && growParams.get("preview") === "1") {
          const params = growParams;
          // RTL is driven by localStorage.appLanguage, read once at startup
          // (DirectionContext) — set it and reload once so Hebrew lays out RTL.
          const previewLang = params.get("lang") === "en" ? "en" : "he";
          if (localStorage.getItem("appLanguage") !== previewLang) {
            localStorage.setItem("appLanguage", previewLang);
            window.location.reload();
            return;
          }
          i18n.changeLanguage(previewLang);
          document.documentElement.lang = previewLang;
          setSidebarData({ communityName: "מגדלי נווה שאנן", last_role_view: "COMMUNITY_MANAGER" });
          setisCommunityManager(true);
          localStorage.setItem("isCommunityManager", true);
          setisCommunityManagementRole(true);
          localStorage.setItem("isCommunityManagementRole", true);
          setUserCommunities([{ id: 1, name: "מגדלי נווה שאנן", image_url: "/mysupplier.png" }]);
          // Setup is complete here; the page's own setup-incomplete mock
          // (`?setup=1`) only affects the Grow page's setup card.
          setGetStartedContent({
            weeklyTasksPreview: true,
            getStartedTasks: { communityShineCompleted: true, supplierCount: { current: 5, target: 3, completed: true }, percentageComplete: 100 },
          });
          return;
        }

        // ⚠️ TEMP dev-only preview for supplier KYC — walks the real supplier
        // app (sidebar, communities index, my communities) through the three
        // KYC states without auth/backend. Opt-in via `?kycPreview=1`, which
        // is remembered so the pages can be clicked between; `?kycPreview=0`
        // turns it off. Remove at graduation.
        if (isKycPreview) {
          const previewLang = getPreviewLanguage(
            new URLSearchParams(window.location.search),
          );
          if (localStorage.getItem("appLanguage") !== previewLang) {
            localStorage.setItem("appLanguage", previewLang);
            window.location.reload();
            return;
          }
          i18n.changeLanguage(previewLang);
          document.documentElement.lang = previewLang;
          localStorage.setItem("isCommunityManagementRole", "false");
          setisCommunityManagementRole(false);
          setSidebarData({
            supplierName: "מאפיית לחם הארץ",
            communityName: "מאפיית לחם הארץ",
            last_role_view: "SUPPLIER",
          });
          setKycStatus(getPreviewKycStatus());
          setIsLoading(false);
          return;
        }

        // ⚠️ TEMP dev-only preview for the supplier index tabs (services /
        // stores / restaurants) — lets the page be walked through as a
        // community manager without auth/backend. Opt-in via `?preview=1`,
        // `?preview=0` turns it off. Remove at graduation.
        const isSupplierIndexRoute = currentRoutePathName === "/find-suppliers";
        const supplierIndexParams = new URLSearchParams(window.location.search);
        if (isSupplierIndexRoute && supplierIndexParams.get("preview") === "1") {
          const previewLang =
            supplierIndexParams.get("lang") === "en" ? "en" : "he";
          if (localStorage.getItem("appLanguage") !== previewLang) {
            localStorage.setItem("appLanguage", previewLang);
            window.location.reload();
            return;
          }
          i18n.changeLanguage(previewLang);
          document.documentElement.lang = previewLang;
          localStorage.setItem("isCommunityManagementRole", "true");
          setisCommunityManagementRole(true);
          setisCommunityManager(true);
          setSidebarData({
            communityName: "מגדלי נווה שאנן",
            last_role_view: "COMMUNITY_MANAGER",
          });
          setUserCommunities([
            { id: 1, name: "מגדלי נווה שאנן", image_url: "/mysupplier.png" },
          ]);
          setIsLoading(false);
          return;
        }

        const isHomeRoute = currentRoutePathName === "/";

        if (!isHomeRoute) {
          const isSupplierRoute = supplierRoutes.some(
            (route) => normalizePath(route.path) === currentRoutePathName,
          );

          const isCommunityManagerRoute = communityRoutes.some(
            (route) => normalizePath(route.path) === currentRoutePathName,
          );

          // /dashboard is registered in BOTH route lists — it is one route
          // rendering whichever dashboard matches the current role. Toggling on
          // a shared route would decide the role FOR the user, and since the
          // supplier branch is tested first a manager who also sells was
          // flipped to SUPPLIER on every visit, making the community-manager
          // dashboard unreachable. Shared routes must leave the role alone.
          const isSharedRoute = isSupplierRoute && isCommunityManagerRoute;

          if (!isSharedRoute && (isSupplierRoute || isCommunityManagerRoute)) {
            const pageView = isSupplierRoute
              ? PAGE_VIEWS.SUPPLIER_MANAGEMENT_VIEW
              : PAGE_VIEWS.COMMUNITY_MANAGEMENT_VIEW;
            await apiPostRequest(endpoints.toggleRoleByPageView, {
              pageView,
            });
          }
        }

        const [sidebarResponse, languagesResponse, profileResponse] =
          await Promise.all([
            apiGetRequest(endpoints.getSidebarData),
            apiGetRequest(endpoints.getAllLanguages),
            apiGetRequest(endpoints.getProfileData),
            fetchGetStartedContent(),
          ]);

        // ⚠️ TEMP (FE-first) — on the Grow page real accounts load as usual; the
        // weekly-tasks sidebar/dashboard card (weeklyTasksPreview) is only on in
        // the full `?preview=1` seed above, and the setup-incomplete mock lives
        // in the Grow page alone. Remove at graduation.

        const sidebar = sidebarResponse?.data || {};
        const langs = languagesResponse?.data?.data || [];
        const profile = profileResponse?.data || {};

        // The hub is the Manager-of-Managers view — portfolio-only sidebar —
        // for users the backend flags as MoM.
        setSidebarData(
          isManagersHubRoute && sidebar.isMoM ? { ...sidebar, momView: true } : sidebar,
        );
        setLanguages(langs);
        setProfileData(profile);
        setKycStatus(sidebar.kyc_status ?? null);

        // Startup language rule (COMY1-397): the server's saved language
        // (`sidebar.language`) wins. The one exception is a newer choice made
        // in this browser whose PUT was never confirmed (the page was
        // reloaded or closed before the PUT answered), which
        // PENDING_LANGUAGE_KEY marks. Then the local choice wins and the PUT
        // is retried; success clears the mark.
        // A toggle whose PUT fails is reverted on the spot and its mark
        // cleared, so a rejected choice never wins on the next load.
        const serverLang = normalizeLangCode(
          langs.find(
            (l) =>
              (l.name || "").trim().toLowerCase() ===
              (sidebar.language || "").trim().toLowerCase(),
          )?.code,
        );
        const pendingCode = localStorage.getItem(PENDING_LANGUAGE_KEY);
        const pendingLanguage =
          pendingCode && findLanguageByCode(langs, pendingCode);
        const pendingLang = normalizeLangCode(pendingLanguage?.code);

        if (pendingLanguage && pendingLang !== serverLang) {
          applyLanguage(pendingLang);
          apiPutRequest(endpoints.updateLanguage, {
            languageId: pendingLanguage.id,
          })
            .then(() => localStorage.removeItem(PENDING_LANGUAGE_KEY))
            .catch((error) =>
              console.error("Failed to re-save the language:", error),
            );
        } else {
          localStorage.removeItem(PENDING_LANGUAGE_KEY);
          if (serverLang) applyLanguage(serverLang);
        }

        if (sidebar.last_role_view !== "SUPPLIER") {
          await fetchUserCommunities();
        }

        const isUserAdmin = sidebar?.last_role_view === "COMMUNITY_MANAGER";
        setisCommunityManager(isUserAdmin);
        localStorage.setItem("isCommunityManager", isUserAdmin);

        const isCMRole =
          sidebar?.last_role_view === "COMMUNITY_MANAGER" ||
          sidebar?.last_role_view === "COMMUNITY_MODERATOR";
        setisCommunityManagementRole(isCMRole);
        localStorage.setItem("isCommunityManagementRole", isCMRole);

        if (sidebar.showWelcomePopup) {
          setShowWelcomePopup(true);
        }
        if (sidebar.showUpdatePopup) {
          setShowUpdatePopup(true);
        }
      } catch (error) {
        console.error(error);
        // Check for 401 or 404 errors in any of the Promise.all calls
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 404)
        ) {
          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 404)
          ) {
            window.location.href = createOnboardingAppUrl({ path: "/welcome" });
          } else if (
            error.response &&
            (error.response.status === 500 || error.response.status === 502)
          ) {
            setSystemError("We are facing issues");
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [fetchGetStartedContent, fetchUserCommunities]);

  // Bumped on every switch so a late failure of an older request never
  // reverts a newer choice.
  const languageRequestRef = useRef(0);

  /**
   * Switch the interface to `targetCode` ('he' | 'en') and save it. The UI
   * changes at once (DirectionContext follows i18n, so the direction flips
   * with it); if the save fails, the previous language comes back.
   * Resolves to true when the switch stuck, false when it was reverted or
   * could not start (the caller shows the error).
   */
  const updateLanguage = useCallback(
    async (targetCode) => {
      const target = normalizeLangCode(targetCode);
      const previous = normalizeLangCode(
        i18n.resolvedLanguage || i18n.language,
      );
      if (target === previous) return true;

      const language = findLanguageByCode(languages, target);
      if (!language) {
        console.error(
          `Cannot switch language: "${target}" is not in the languages list`,
          languages,
        );
        return false;
      }

      const requestId = ++languageRequestRef.current;
      applyLanguage(target);
      localStorage.setItem(PENDING_LANGUAGE_KEY, target);

      try {
        await apiPutRequest(endpoints.updateLanguage, {
          languageId: language.id,
        });
        if (requestId === languageRequestRef.current) {
          localStorage.removeItem(PENDING_LANGUAGE_KEY);
        }
        return true;
      } catch (error) {
        console.error("Failed to update language:", error);
        if (error?.response?.status === 401) {
          window.location.href = createOnboardingAppUrl({ path: "/welcome" });
          return false;
        }
        // A 404 here means "language not found", not a missing account, so
        // unlike before it no longer sends the user to the welcome page.
        if (requestId === languageRequestRef.current) {
          localStorage.removeItem(PENDING_LANGUAGE_KEY);
          if (previous) applyLanguage(previous);
        }
        return false;
      }
    },
    [languages],
  );

  // Re-check for update popup when user returns to the tab
  const checkForUpdatePopup = useCallback(async () => {
    try {
      const response = await apiGetRequest(endpoints.getSidebarData);
      const sidebar = response?.data || {};
      if (sidebar.showUpdatePopup) {
        setShowUpdatePopup(true);
      }
    } catch (error) {
      // Silently fail - not critical
    }
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdatePopup();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    emitter.on("fetch-get-started-content", fetchGetStartedContent);
    emitter.on("check-update-popup", checkForUpdatePopup);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      emitter.off("fetch-get-started-content", fetchGetStartedContent);
      emitter.off("check-update-popup", checkForUpdatePopup);
    };
  }, [fetchGetStartedContent, checkForUpdatePopup]);

  return (
    <AppContext.Provider
      value={{
        sidebarData,
        isCommunityManager,
        isCommunityManagementRole,
        languages,
        updateLanguage,
        showWelcomePopup,
        setShowWelcomePopup,
        showUpdatePopup,
        setShowUpdatePopup,
        isLoading,
        profileData,
        getStartedContent,
        userCommunities,
        fetchGetStartedContent,
        kycStatus,
        refreshKycStatus,
        // ⚠️ TEMP DEV-PHASE PREVIEW — only the KYC preview switcher writes this.
        setKycStatus,
        systemError,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
